/**
 * Graph Layout Utility
 * Handles node positioning and edge path calculation
 */

/**
 * Calculate positions for nodes using a hierarchical layout
 * Supports both automatic layout and manual positioning
 * @param {Array} nodes - Array of node objects with id, type, etc.
 *                        Nodes can have optional x, y properties for manual positioning
 * @param {Array} edges - Array of edge objects with from, to
 * @returns {Object} Map of nodeId -> {x, y}
 */
export function calculateLayout(nodes, edges) {
  const nodeMap = new Map();
  const nodePositions = new Map();
  
  // First, check for nodes with manual positions
  const manualPositionNodes = [];
  const autoLayoutNodes = [];
  
  nodes.forEach(node => {
    if (node.x !== undefined && node.y !== undefined) {
      // Node has manual position
      manualPositionNodes.push(node);
      nodePositions.set(node.id, { x: node.x, y: node.y });
    } else {
      // Node needs automatic layout
      autoLayoutNodes.push(node);
    }
  });
  
  // If all nodes have manual positions, return early
  if (autoLayoutNodes.length === 0) {
    return nodePositions;
  }
  
  // Initialize node map for auto-layout nodes only
  autoLayoutNodes.forEach(node => {
    nodeMap.set(node.id, {
      ...node,
      level: -1,
      children: [],
      parents: []
    });
  });
  
  // Build graph structure (only for auto-layout nodes)
  edges.forEach(edge => {
    const fromNode = nodeMap.get(edge.from);
    const toNode = nodeMap.get(edge.to);
    
    if (fromNode && toNode) {
      fromNode.children.push(edge.to);
      toNode.parents.push(edge.from);
    }
  });
  
  // Find root nodes (nodes with no parents)
  const rootNodes = Array.from(nodeMap.values()).filter(node => node.parents.length === 0);
  
  // If no root nodes, pick the first node as root
  if (rootNodes.length === 0 && autoLayoutNodes.length > 0) {
    rootNodes.push(nodeMap.get(autoLayoutNodes[0].id));
  }
  
  // Assign levels using BFS
  const queue = [];
  rootNodes.forEach(node => {
    node.level = 0;
    queue.push(node.id);
  });
  
  while (queue.length > 0) {
    const currentId = queue.shift();
    const currentNode = nodeMap.get(currentId);
    
    currentNode.children.forEach(childId => {
      const childNode = nodeMap.get(childId);
      if (childNode && (childNode.level === -1 || childNode.level <= currentNode.level)) {
        childNode.level = currentNode.level + 1;
        queue.push(childId);
      }
    });
  }
  
  // Group nodes by level
  const levelGroups = new Map();
  nodeMap.forEach((node, id) => {
    const level = Math.max(0, node.level);
    if (!levelGroups.has(level)) {
      levelGroups.set(level, []);
    }
    levelGroups.get(level).push(id);
  });
  
  // Calculate positions for auto-layout nodes
  const HORIZONTAL_SPACING = 100;
  const VERTICAL_SPACING = 200;
  const START_X = 50;
  const START_Y = 80;
  
  levelGroups.forEach((nodeIds, level) => {
    const y = START_Y + level * VERTICAL_SPACING;
    const totalWidth = nodeIds.length * HORIZONTAL_SPACING;
    const startX = START_X + (Math.max(...Array.from(levelGroups.keys())) * HORIZONTAL_SPACING - totalWidth) / 2;
    
    nodeIds.forEach((nodeId, index) => {
      const x = startX + index * HORIZONTAL_SPACING;
      nodePositions.set(nodeId, { x, y });
    });
  });
  
  return nodePositions;
}

const NODE_WIDTH = 180;
const NODE_HEIGHT = 70;

export { NODE_WIDTH, NODE_HEIGHT };
/**
 * Calculate SVG path for an edge between two nodes
 * @param {Object} fromPos - {x, y} position of source node
 * @param {Object} toPos - {x, y} position of target node
 * @param {number} nodeWidth - Width of the node
 * @param {number} nodeHeight - Height of the node
 * @returns {string} SVG path string
 */
export function calculateEdgePath(fromPos, toPos, nodeWidth = NODE_WIDTH, nodeHeight = NODE_HEIGHT) {
  const fromX = fromPos.x + nodeWidth / 2;
  const fromY = fromPos.y + nodeHeight;
  const toX = toPos.x + nodeWidth / 2;
  const toY = toPos.y;
  
  // Calculate control points for smooth curve
  const dx = toX - fromX;
  const dy = toY - fromY;
  const controlPoint1Y = fromY + Math.max(50, Math.abs(dy) * 0.3);
  const controlPoint2Y = toY - Math.max(50, Math.abs(dy) * 0.3);
  
  return `M ${fromX} ${fromY} C ${fromX} ${controlPoint1Y}, ${toX} ${controlPoint2Y}, ${toX} ${toY}`;
}

/**
 * Get edge connection points (for arrow heads)
 * @param {Object} fromPos - {x, y} position of source node
 * @param {Object} toPos - {x, y} position of target node
 * @param {number} nodeWidth - Width of the node
 * @param {number} nodeHeight - Height of the node
 * @returns {Object} {start: {x, y}, end: {x, y}}
 */
export function getEdgeConnectionPoints(fromPos, toPos, nodeWidth = NODE_WIDTH, nodeHeight = NODE_HEIGHT) {
  return {
    start: {
      x: fromPos.x + nodeWidth / 2,
      y: fromPos.y + nodeHeight
    },
    end: {
      x: toPos.x + nodeWidth / 2,
      y: toPos.y
    }
  };
}

